<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { EVENT_STATUS } from "~/constants";
import useEventLock from "~/hooks/events/use-event-lock";
import useEventStore from "~/stores/event";
import useUserStore from "~/stores/user";
import { getPriceLabel, getReleaseDateTitle } from "~/utils/subscription";

export interface EventPosterProps {
  /**
   * Shows "Sign In" prompt with blurred background
   */
  status?: TEventStatus;
  commerceCoverage: TCommerceCategory[];
  team1LogoUrl?: string;
  team2LogoUrl?: string;
  team1Color?: string;
  team2Color?: string;
  isEventAvailableForUser?: boolean;
  isEventAvailableInUserRegion?: boolean;
  imageUrl?: string;
  team1Name?: string;
  team2Name?: string;
}
const props = withDefaults(defineProps<EventPosterProps>(), {
  team1Color: "#1C2535",
  team2Color: "#2C374B",
});
const { t, locale: currentLocale } = useI18n();
const route = useRoute();
const router = useRouter();
const eventStore = useEventStore();
const userStore = useUserStore();
const breakpoints = useBreakpoints(breakpointsTailwind);
const appConfig = useRuntimeConfig().public;
const { lockMessage, isLocked, isGeoBlocked, isAuthLock } = useEventLock({
  commerceCoverage: props.commerceCoverage,
  isEventAvailableForUser: props.isEventAvailableForUser,
  isEventAvailableInUserRegion: props.isEventAvailableInUserRegion,
});

const isLoggedIn = computed(() => Boolean(userStore.user));
const isRtl = computed(() => currentLocale.value === "he");
const sm = breakpoints.smaller("sm");
const md = breakpoints.between("sm", "md");
const lg = breakpoints.between("md", "lg");
const logoSizes = computed(() => {
  if (lg.value)
    return 142;
  if (md.value)
    return 128;
  if (sm.value)
    return 96;
  return 188;
});
const commerceOptions = computed(() => eventStore.commerceOptions);
const isAccessCodesAvailable = appConfig.APP_CUSTOMER.enable_accessCodes;
const hasCommerceOptions = !commerceOptions.value.length;
const isPurchaseProcessing = commerceOptions.value.some(option => option.purchaseProcessing);
const commerceLowestPrice = computed(() => {
  if (commerceOptions.value && commerceOptions.value.length) {
    const validItems = commerceOptions.value.filter(item => !getReleaseDateTitle(item.startDate));

    if (validItems.length === 0)
      return "N/A";

    const { price, currency } = validItems.reduce((p, v) => p.price < v.price ? p : v);
    return getPriceLabel(price, currency.toUpperCase());
  }
  return "N/A";
});
const isLive = computed(() => props.status === EVENT_STATUS.LIVE);
const isUpcoming = computed(() => props.status === EVENT_STATUS.UPCOMING);
const isEventStartingSoon = computed(() => eventStore.isEventStartingSoon);
const isCommerceModalAvailable = computed(() => isLoggedIn.value && !isGeoBlocked.value);
</script>

<template>
  <div class="relative">
    <div
      v-if="isLive || isUpcoming"
      class="absolute z-30 flex items-center justify-center text-caption-medium text-main rounded-full background-base m-4 py-2px px-1"
    >
      <template v-if="isLive">
        <div class="h-1 w-1 background-error rounded-full mx-1 animate-pulse" />
        <span class="mx-1">{{ t("labels.live") }}</span>
      </template>
      <template v-if="isUpcoming">
        <IconCalendar class="mx-1" />
        <span class="mx-1">{{ t("labels.upcoming") }}</span>
      </template>
    </div>

    <div v-if="isLocked" class="absolute inset-0 z-20 flex items-center justify-center flex-col text-main mb-2">
      <IconLock width="24" height="32" class="text-white" />
      <p class="m-6 text-sm font-bold sm:text-base text-white text-center">
        {{ lockMessage }}
      </p>
      <div v-if="isCommerceModalAvailable" class="flex justify-between flex-nowrap gap-1 sm:gap-3">
        <CommerceModalSubPPV>
          <template #trigger="{ openModal }">
            <BaseButton
              type="primary" name="price" :disabled="isPurchaseProcessing || hasCommerceOptions" class="font-bold text-white text-xs sm:text-base"
              @click="openModal()"
            >
              {{ isPurchaseProcessing ? t('pages.account_billing.payment_in_progress') : t("labels.ppv_price", { price: commerceLowestPrice }) }}
            </BaseButton>
          </template>
        </CommerceModalSubPPV>
        <CommerceModalRedeemCode v-if="isAccessCodesAvailable">
          <template #trigger="{ openModal }">
            <BaseButton
              type="secondary"
              name="access-code"
              class="font-bold text-white text-xs sm:text-base"
              @click="openModal()"
            >
              {{ t("labels.access_code") }}
            </BaseButton>
          </template>
        </CommerceModalRedeemCode>
      </div>
      <AuthButton
        v-else-if="isAuthLock"
        mode="sign-in"
        class="font-bold text-white"
        :title="t('labels.sign_in')"
        :redirect-url="route.fullPath"
        :on-success="() => router.go(0)"
      />
    </div>

    <div v-else-if="isEventStartingSoon" class="absolute inset-0 z-20 flex items-center justify-center flex-col ">
      <div class="flex w-full h-full items-center flex-col justify-center">
        <IconSpinnerBlade :size="40" />
        <p class="mt-6 text-white text-xs sm:text-lg xl:text-xl font-semibold">
          {{ t('pages.events.event_will_start_in_a_few_min') }}
        </p>
      </div>
    </div>

    <div :class="{ 'backdrop-blur': isLocked || isEventStartingSoon }">
      <div v-if="props.imageUrl" class="aspect-video">
        <img :src="props.imageUrl" class="w-full h-full">
      </div>
      <div v-else>
        <div class="absolute inset-0 flex items-center justify-around">
          <BaseLogo
            :src="props.team1LogoUrl"
            :width="logoSizes"
            :height="logoSizes"
            full-size
            no-background
            with-initials
            :title="props.team1Name"
            fit="contain"
          />
          <BaseLogo
            :src="props.team2LogoUrl"
            :width="logoSizes"
            :height="logoSizes"
            full-size
            no-background
            with-initials
            :title="props.team2Name"
            fit="contain"
          />
        </div>

        <svg viewBox="0 0 920 519" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
          <defs>
            <path id="bpfga9iama" d="M4 0h912a4 4 0 0 1 4 4v515H0V4a4 4 0 0 1 4-4z" />
          </defs>
          <g fill="none" fill-rule="evenodd">
            <mask id="0wcn2gvh1b" fill="#fff">
              <use xlink:href="#bpfga9iama" />
            </mask>
            <use :fill="isRtl ? props.team1Color : props.team2Color" xlink:href="#bpfga9iama" />
            <path
              :fill="isRtl ? props.team2Color : props.team1Color"
              mask="url(#0wcn2gvh1b)"
              d="M-12.323-8.097v544.252l402.772-4.726 94.996-267.4-53.025-13.225 96.31-261.527z"
            />
            <!-- eslint-disable-next-line max-len -->
            <path d="M447.149 247.956c5.012-.016 10.024-.162 15.036-.07 5.013.059 10.024.06 15.037.216l15.037.393 14.407.376-5.245 12.847-26.303 64.418c-8.785 21.466-17.483 42.965-26.396 64.384l-26.609 64.302c-8.984 21.393-17.862 42.823-26.945 64.178 7.099-22.095 14.403-44.115 21.6-66.174l21.934-66.052c7.27-22.032 14.756-43.985 22.152-65.969l17.66-52.354-1.292.034c-5.013.155-10.024.157-15.037.216-5.012.092-10.024-.054-15.036-.07-5.013-.047-10.024-.229-15.037-.335l-14.564-.312 5.17-13.076 25.24-63.831c8.455-21.261 16.765-42.576 25.343-63.794l25.584-63.707c8.653-21.19 17.19-42.423 25.947-63.576-6.694 21.897-13.614 43.713-20.413 65.57l-20.776 65.44c-6.874 21.832-14.02 43.565-21.017 65.352l-16.69 51.617c.403-.005.808-.02 1.213-.023" fill="var(--base-background-color)" mask="url(#0wcn2gvh1b)" />
          </g>
        </svg>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.backdrop-blur {
  filter: blur(3px) brightness(0.56);
}
</style>
